// Member roster spreadsheet import for association admins (Users -> Import
// spreadsheet). Reading and checking happen in the browser (src/lib/rosterImport.js);
// the write is one all-or-nothing RPC, import_roster_rows, which re-checks that the
// caller is an admin of the association and that every home belongs to it.
import { supabase } from '../lib/supabase';
import { parseCsv, readRoster, rosterTemplateCsv } from '../lib/rosterImport';

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const EXCEL = /\.(xlsx|xlsm|xls)$/i;

async function fileToCsvText(file) {
  if (!file) throw new Error('Choose a file first.');
  if (file.size > MAX_FILE_BYTES) throw new Error('That file is larger than 5 MB. Split it, or remove extra sheets and columns.');
  if (EXCEL.test(file.name)) {
    // Loaded on demand: only admins importing a spreadsheet pay for the Excel reader.
    const XLSX = await import('xlsx');
    const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    if (!sheet) throw new Error('That workbook has no sheets.');
    return XLSX.utils.sheet_to_csv(sheet, { blankrows: false });
  }
  if (/\.(csv|txt)$/i.test(file.name) || /csv|text\/plain/.test(file.type)) return file.text();
  throw new Error('Use the template saved as .csv or .xlsx.');
}

export const rosterImportService = {
  /** Downloads the blank template (with three EXAMPLE rows the import skips). */
  downloadTemplate(fileName = 'member-roster-template.csv') {
    const blob = new Blob([rosterTemplateCsv()], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },

  /** File -> { people, issues, examplesSkipped, unknownHeaders, missingHeaders }. */
  async readFile(file) {
    return readRoster(parseCsv(await fileToCsvText(file)));
  },

  /** The association's homes, for matching rows to existing properties. */
  async listHomes(hoaId) {
    if (!hoaId) throw new Error('Association not loaded yet.');
    const { data, error } = await supabase
      .from('properties')
      .select('id, situs_address, unit_number')
      .eq('hoa_id', hoaId)
      .range(0, 4999);
    if (error) throw error;
    return data || [];
  },

  /** rows from planRosterImport -> { homes_created, people_added, people_skipped }. */
  async importRows(hoaId, rows) {
    const { data, error } = await supabase.rpc('import_roster_rows', { p_hoa_id: hoaId, p_rows: rows });
    if (error) throw new Error(error.message || 'Import failed; nothing was added.');
    return data;
  },
};

export default rosterImportService;
